import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, X, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "@/components/ConfirmModal";
import { V3Leads } from "@/components/dashboard/V3Leads";

export const LEAD_STATUSES = [
  "nuovo",
  "contattato",
  "da richiamare",
  "appuntamento",
  "cliente",
  "non interessato",
  "perso",
];
const badgeClass = (s) => `vg-badge vg-badge-${s.replace(/\s+/g, "-")}`;

function journeyProgress(lead) {
  let count = 0;
  if (lead?.orizzonti_completed) count += 1;
  if (lead?.valutapro_completed_at) count += 1;
  if (lead?.partenza) count += 1;
  return count;
}

function phoneVisible(lead) {
  // Backend masks phone when the lead is Orizzonti-only WITHOUT explicit call request.
  // If _orizzonti_masked flag is set OR the phone field is empty, treat as masked.
  if (lead?._orizzonti_masked) return false;
  return !!(lead?.telefono && String(lead.telefono).trim());
}

export default function AgentLeads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/agent/leads");
      setLeads(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (lead_id, stato) => {
    try {
      await api.put(`/agent/leads/${lead_id}/status`, { stato_lead: stato });
      setLeads((arr) => arr.map((l) => l.id === lead_id ? { ...l, stato_lead: stato } : l));
      toast.success("Stato aggiornato");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/agent/leads/${id}`);
      setSelected(r.data);
    } catch { toast.error("Errore"); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/leads/${pendingDelete.id}`);
      setLeads((arr) => arr.filter((l) => l.id !== pendingDelete.id));
      if (selected?.id === pendingDelete.id) setSelected(null);
      toast.success("Lead eliminato");
    } catch { toast.error("Errore eliminazione"); }
    setPendingDelete(null);
  };

  return (
    <div className="space-y-6 fade-slide">
      <div>
        <h1 className="text-3xl font-bold text-[#0A0A0A]">I tuoi lead</h1>
        <p className="text-gray-500 mt-1">{leads.length} lead dai percorsi classici</p>
      </div>

      <V3Leads />

      <div className="vg-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-gray-400">Caricamento…</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Nessun lead per ora. Condividi il tuo link referral per riceverne.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "#FAFAFA" }}>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3">Indirizzo</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const step = journeyProgress(l);
                  return (
                  <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-gray-500">{new Date(l.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[#0A0A0A]">{l.nome}</p>
                      <p className="text-xs text-gray-500">{l.email}</p>
                      <div className="flex flex-wrap gap-1 mt-1 items-center">
                        {step > 0 && (
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#B40101]/10 text-[#B40101] uppercase tracking-widest"
                            title="Progresso percorso ValutaGenio"
                            data-testid={`lead-journey-${l.id}`}
                          >
                            {step}/3
                          </span>
                        )}
                        {l.valutapro_completed_at && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700" data-testid={`lead-badge-vp-${l.id}`}>ValutaPro</span>
                        )}
                        {/* Badge modalità: se coesistono entrambe le analisi mostra due badge. */}
                        {(() => {
                          const currentMod = (l.modalita || l.valutapro_modalita || "").toString().toLowerCase();
                          const hasVendita = !!(l.valutapro_analyses && l.valutapro_analyses.vendita)
                            || (l.valutapro_completed_at && currentMod !== "affitto");
                          const hasAffitto = !!(l.valutapro_analyses && l.valutapro_analyses.affitto)
                            || (l.valutapro_completed_at && currentMod === "affitto");
                          if (!hasVendita && !hasAffitto) return null;
                          return (
                            <>
                              {hasVendita && (
                                <span
                                  className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest"
                                  style={{ background: "#FEF2F2", color: "#B40101" }}
                                  title="Analisi ValutaPro Vendita"
                                  data-testid={`lead-badge-mode-vendita-${l.id}`}
                                >
                                  Vendita
                                </span>
                              )}
                              {hasAffitto && (
                                <span
                                  className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest"
                                  style={{ background: "#EFF6FF", color: "#1E3A8A" }}
                                  title="Analisi ValutaPro Affitto"
                                  data-testid={`lead-badge-mode-affitto-${l.id}`}
                                >
                                  Affitto
                                </span>
                              )}
                            </>
                          );
                        })()}
                        {l.partenza && (
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: l.partenza.semaforo === "verde" ? "#ECFDF5" : l.partenza.semaforo === "giallo" ? "#FFFBEB" : "#FEF2F2", color: l.partenza.semaforo === "verde" ? "#065F46" : l.partenza.semaforo === "giallo" ? "#92400E" : "#991B1B" }}
                            data-testid={`lead-badge-pp-${l.id}`}
                          >
                            PartenzaPro · {l.partenza.semaforo}
                          </span>
                        )}
                        {l.orizzonti_completed && (
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#3730A3]"
                            title={l.orizzonti_v2?.call_status === "requested" ? "Call richiesta" : "Contatto non richiesto"}
                            data-testid={`lead-badge-oz-${l.id}`}
                          >
                            O completato{l.orizzonti_v2?.call_status === "requested" ? " · call" : ""}
                          </span>
                        )}
                        {!phoneVisible(l) && (
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wider"
                            title="Il contatto telefonico non è ancora stato richiesto"
                            data-testid={`lead-phone-masked-${l.id}`}
                          >
                            Contatto non richiesto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{l.indirizzo || <span className="text-gray-400 italic">—</span>}</td>
                    <td className="px-4 py-3">
                      <select
                        className={`${badgeClass(l.stato_lead)} border-0 cursor-pointer`}
                        value={l.stato_lead}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        data-testid={`lead-status-${l.id}`}
                      >
                        {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => openDetail(l.id)} className="vg-btn-secondary inline-flex items-center gap-1 text-xs px-3 py-2" data-testid={`lead-view-${l.id}`}>
                          <Eye size={14} /> Dettaglio
                        </button>
                        <button onClick={() => setPendingDelete(l)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-[10px] border border-red-200 text-[#B40101] hover:bg-red-50 transition-colors" data-testid={`lead-delete-${l.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <LeadDetail
          lead={selected}
          onClose={() => setSelected(null)}
          onDelete={() => setPendingDelete(selected)}
          onSaved={(updated) => {
            setLeads((arr) => arr.map((l) => l.id === updated.id ? updated : l));
            setSelected(updated);
          }}
        />
      )}

      <ConfirmModal
        open={!!pendingDelete}
        title="Elimina lead"
        message="Sei sicuro di voler eliminare questo lead? L'operazione non è reversibile."
        confirmLabel="Conferma elimina"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        testid="lead-delete-modal"
      />
    </div>
  );
}

export function LeadDetail({ lead, onClose, onSaved, onDelete }) {
  const [note, setNote] = useState(lead.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/agent/leads/${lead.id}/note`, { note });
      const updated = { ...lead, note };
      onSaved(updated);
      toast.success("Note salvate");
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[12px] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-xl font-bold text-[#0A0A0A]">Dettaglio lead</h3>
          <button onClick={onClose} data-testid="lead-detail-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <Section title="Contatto">
            <Row label="Nome" value={lead.nome} />
            <Row label="Email" value={lead.email} />
            <Row
              label="Telefono"
              value={phoneVisible(lead) ? lead.telefono : <span className="text-gray-400 italic">Contatto non richiesto</span>}
            />
          </Section>
          {lead.valutapro_completed_at && (
            <Section title="Immobile">
              <Row label="Modalità" value={((lead.modalita || lead.valutapro_modalita || "vendita")).toString().toUpperCase()} />
              <Row label="Indirizzo" value={lead.indirizzo} />
              <Row label="Tipologia" value={lead.tipologia} />
              <Row label="Metratura" value={`${lead.mq} mq`} />
              <Row label="Stato" value={lead.stato_immobile} />
              <Row label="Piano" value={lead.piano} />
              <Row label="Pertinenze" value={lead.pertinenze?.join(", ") || "Nessuna"} />
              <Row label="Indice vendibilità" value={`${lead.indice_vendibilita}/100`} />
              <Row label="Tempistica" value={lead.tempistica} />
            </Section>
          )}
          {lead.analisi_mercato && (
            <Section title="Analisi di mercato">
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{lead.analisi_mercato}</p>
            </Section>
          )}
          {(lead.modalita === "affitto" || lead.valutapro_modalita === "affitto")
            ? (lead.stima_affitto_testo && (
                <Section title="Stima del canone (affitto)">
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{lead.stima_affitto_testo}</p>
                </Section>
              ))
            : (lead.stima_valore && (
                <Section title="Stima di valore">
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{lead.stima_valore}</p>
                </Section>
              ))}
          {lead.punti_critici?.length > 0 && (
            <Section title="Punti critici">
              <ul className="space-y-2 text-sm text-gray-600">
                {lead.punti_critici.map((c, i) => <li key={i}>• {c}</li>)}
              </ul>
            </Section>
          )}
          {lead.partenza && (
            <Section title={`PartenzaPro · Semaforo ${lead.partenza.semaforo?.toUpperCase()} (${lead.partenza.punteggio})`}>
              {lead.partenza.report ? (
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line" data-testid="lead-partenza-report">{lead.partenza.report}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">Report in generazione…</p>
              )}
            </Section>
          )}
          {lead.orizzonti_v2 && (
            <Section title="OrizzontiPro">
              <div className="text-xs text-gray-500 mb-2" data-testid="lead-orizzonti-status">
                Stato analisi: <strong className="text-[#0A0A0A]">{lead.orizzonti_v2.status}</strong>
                · Contatto: <strong className="text-[#0A0A0A]">{lead.orizzonti_v2.call_status === "requested" ? "Call richiesta" : "Non richiesto"}</strong>
              </div>
              {lead.orizzonti_v2.story_excerpt && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Racconto originale (estratto)</div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="lead-orizzonti-story">{lead.orizzonti_v2.story_excerpt}</p>
                </div>
              )}
              {lead.orizzonti_v2.orientation_profile && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Profilo di orientamento</div>
                  <ul className="text-sm text-gray-700 space-y-1" data-testid="lead-orizzonti-profile">
                    {Object.entries(lead.orizzonti_v2.orientation_profile).slice(0, 8).map(([k, v]) => (
                      <li key={k}><strong>{k.replace(/_/g, " ")}:</strong> {String(v)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {lead.orizzonti_v2.structured_answers && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Cinque risposte</div>
                  <ul className="text-sm text-gray-700 space-y-1" data-testid="lead-orizzonti-answers">
                    {Object.entries(lead.orizzonti_v2.structured_answers).map(([k, v]) => (
                      <li key={k}><strong>{k}:</strong> {Array.isArray(v) ? v.join(", ") : String(v)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Note interne</p>
            <textarea
              className="vg-input min-h-[100px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Aggiungi note interne..."
              data-testid="lead-note-input"
            />
            <div className="mt-3 flex gap-3 justify-between flex-wrap">
              <button className="vg-btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving} data-testid="lead-note-save">
                <Save size={14} /> {saving ? "Salvataggio…" : "Salva note"}
              </button>
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-[12px] border border-red-200 text-[#B40101] hover:bg-red-50 transition-colors text-sm font-semibold"
                  data-testid="lead-detail-delete"
                >
                  <Trash2 size={14} /> Elimina lead
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-[#B40101] mb-3">{title}</p>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-400">{label}</span>
      <span className="text-[#0A0A0A] font-medium text-right">{value}</span>
    </div>
  );
}
